import React, { useEffect, useState, Fragment } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'

import { httpOk, GET, PUT } from 'helpers/api'
import { notify } from 'helpers/helpers'

function hasPrintRouteDraftConflict(prepIds = [], otsIds = []) {
	return prepIds.length > 0 && otsIds.length > 0
}

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const [printers, setPrinters] = useState([])
	const [routesByDevice, setRoutesByDevice] = useState({})
	const [preparationPlaces, setPreparationPlaces] = useState([])
	const [orderTypeSubs, setOrderTypeSubs] = useState([])
	const [modalPP, setModalPP] = useState({ bool: false, device_id: null, device_name: '' })
	const [modalOTS, setModalOTS] = useState({ bool: false, device_id: null, device_name: '' })
	const [draftPrepIds, setDraftPrepIds] = useState([])
	const [draftOtsIds, setDraftOtsIds] = useState([])

	async function loadData(posId = reduxAccount.pos_id) {
		if (!posId) {
			setPrinters([])
			setRoutesByDevice({})
			return
		}

		const [routesResponse, printersResponse, placesResponse, orderTypeSubsResponse] = await Promise.all([
			GET(`/print-routes?pos_id=${posId}`, {}, { loader: true }),
			GET(`/print-routes/pos-printers/${posId}`, {}, { loader: false }),
			GET(`/helper/preparation-places?pos_id=${posId}`, {}, { loader: false }),
			GET('/helper/order-type-subs', {}, { loader: false }),
		])

		if (httpOk(printersResponse)) setPrinters(printersResponse.data)
		if (httpOk(placesResponse)) setPreparationPlaces(placesResponse.data)
		if (httpOk(orderTypeSubsResponse)) setOrderTypeSubs(orderTypeSubsResponse.data)

		if (httpOk(routesResponse)) {
			const map = {}
			routesResponse.data.forEach((item) => {
				map[item.device_id] = item
			})
			setRoutesByDevice(map)
		}
	}

	async function loadDeviceRouteIds(deviceId) {
		const response = await GET(`/print-routes/${deviceId}`, {}, { loader: true })
		if (!httpOk(response)) {
			return { preparation_place_ids: [], order_type_sub_ids: [] }
		}

		return {
			preparation_place_ids: response.data.preparation_place_ids || [],
			order_type_sub_ids: response.data.order_type_sub_ids || [],
		}
	}

	async function toggleModalPP(bool = false, printer = null) {
		if (bool && printer) {
			const route = await loadDeviceRouteIds(printer.id)
			setDraftPrepIds(route.preparation_place_ids)
			setDraftOtsIds(route.order_type_sub_ids)
			setModalPP({ bool: true, device_id: printer.id, device_name: printer.name })
			return
		}

		setModalPP({ bool: false, device_id: null, device_name: '' })
		setDraftPrepIds([])
	}

	async function toggleModalOTS(bool = false, printer = null) {
		if (bool && printer) {
			const route = await loadDeviceRouteIds(printer.id)
			setDraftPrepIds(route.preparation_place_ids)
			setDraftOtsIds(route.order_type_sub_ids)
			setModalOTS({ bool: true, device_id: printer.id, device_name: printer.name })
			return
		}

		setModalOTS({ bool: false, device_id: null, device_name: '' })
		setDraftOtsIds([])
	}

	function notifyRouteConflict(deviceName) {
		notify(t('print_route_conflict_error', { name: deviceName || '' }), 'error')
	}

	function handleTogglePrep(placeId, checked) {
		if (checked && draftOtsIds.length > 0) {
			notify(t('print_route_disable_order_types_first'), 'error')
			return
		}

		setDraftPrepIds((prev) => {
			if (checked) return [...prev, placeId]
			return prev.filter((id) => id !== placeId)
		})
	}

	function handleToggleOts(orderTypeSubId, checked) {
		if (checked && draftPrepIds.length > 0) {
			notify(t('print_route_disable_prep_first'), 'error')
			return
		}

		setDraftOtsIds((prev) => {
			if (checked) return [...prev, orderTypeSubId]
			return prev.filter((id) => id !== orderTypeSubId)
		})
	}

	async function saveRoutes(deviceId, deviceName = '') {
		if (!deviceId) return false

		if (hasPrintRouteDraftConflict(draftPrepIds, draftOtsIds)) {
			notifyRouteConflict(deviceName)
			return false
		}

		if (draftPrepIds.length === 0 && draftOtsIds.length === 0) {
			notify(t('print_routes_required'), 'error')
			return false
		}

		const response = await PUT('/print-routes', {
			device_id: deviceId,
			preparation_place_ids: draftPrepIds,
			order_type_sub_ids: draftOtsIds,
		}, { loader: true })

		if (httpOk(response)) {
			loadData(reduxAccount.pos_id)
			return true
		}

		return false
	}

	async function saveModalPP() {
		const saved = await saveRoutes(modalPP.device_id, modalPP.device_name)
		if (saved) toggleModalPP()
	}

	async function saveModalOTS() {
		const saved = await saveRoutes(modalOTS.device_id, modalOTS.device_name)
		if (saved) toggleModalOTS()
	}

	const modalRouteDraftConflict = hasPrintRouteDraftConflict(draftPrepIds, draftOtsIds)

	function formatRouteList(items = []) {
		if (!items?.length) return '-'
		return items.map((item) => item.name).join(', ')
	}

	useEffect(() => {
		loadData(reduxAccount.pos_id)
	}, [reduxAccount.pos_id]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('print_routes')} [{printers.length}]
				</h4>
			</div>

			<p className="text-muted mb-3">{t('print_routes_kitchen_only_hint')}</p>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: '33px' }} className="text-center">#</th>
									<th style={{ width: '22%' }}>{t('printer')}</th>
									<th style={{ width: '12%' }}>{t('address')}</th>
									<th style={{ width: '28%' }}>{t('preparation_places')}</th>
									<th style={{ width: '22%' }}>{t('order_types')}</th>
									<th style={{ minWidth: '220px' }}></th>
								</tr>
							</thead>
							<tbody>
								{printers?.length > 0 ? printers.map((item, index) => {
									const route = routesByDevice[item.id]

									return (
										<tr key={item.id || index}>
											<td className="text-center">{index + 1}</td>
											<td className="td-ellipsis">{item.name}</td>
											<td className="td-ellipsis">{route?.device_ip || '-'}</td>
											<td className="td-ellipsis">{formatRouteList(route?.preparation_places)}</td>
											<td className="td-ellipsis">{formatRouteList(route?.order_type_subs)}</td>
											<td>
												<div className="d-flex justify-content-end gap-2">
													<button type="button" className="btn btn-warning btn-sm"
														onClick={() => toggleModalOTS(true, item)}>
														{t('order_types')}
													</button>
													<button type="button" className="btn btn-success btn-sm"
														onClick={() => toggleModalPP(true, item)}>
														{t('preparation_places')}
													</button>
												</div>
											</td>
										</tr>
									)
								}) : (
									<tr>
										<td colSpan={6} className="text-center text-muted">{t('no_data')}</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<Modal show={modalPP.bool} animation={false} centered size="md"
				onHide={() => toggleModalPP()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('preparation_places')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{modalPP.device_name &&
						<p className="text-muted mb-3">{modalPP.device_name}</p>
					}
					<p className="text-danger mb-2" style={{ fontSize: '14px', lineHeight: 1.4 }}>
						{t('print_route_prep_place_warning')}
					</p>
					{draftOtsIds.length > 0 &&
						<p className="text-danger mb-3" style={{ fontSize: '14px', lineHeight: 1.4 }}>
							{t('print_route_disable_order_types_first')}
						</p>
					}
					{modalRouteDraftConflict &&
						<p className="text-danger mb-3" style={{ fontSize: '14px', lineHeight: 1.4 }}>
							{t('print_route_conflict_error', { name: modalPP.device_name || '' })}
						</p>
					}
					<div className="addition-wrapper gap-2 no-scroll">
						{preparationPlaces.map((item) => (
							<Fragment key={item.id}>
								<div className="d-flex justify-content-between">
									<b className="vertical-center">{item.name}</b>
									<div className="form-check form-switch form-switch-lg">
										<input type="checkbox" className="form-check-input"
											checked={draftPrepIds.includes(item.id)}
											disabled={draftOtsIds.length > 0 && !draftPrepIds.includes(item.id)}
											onChange={(e) => handleTogglePrep(item.id, e.target.checked)} />
									</div>
								</div>
							</Fragment>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-outline-warning"
						onClick={() => toggleModalPP()}>
						{t('cancel')}
					</button>
					<button type="button" className="btn btn-primary"
						disabled={modalRouteDraftConflict}
						onClick={() => saveModalPP()}>
						{t('save')}
					</button>
				</Modal.Footer>
			</Modal>

			<Modal show={modalOTS.bool} animation={false} centered size="md"
				onHide={() => toggleModalOTS()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('order_types')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{modalOTS.device_name &&
						<p className="text-muted mb-3">{modalOTS.device_name}</p>
					}
					<p className="text-danger mb-2" style={{ fontSize: '14px', lineHeight: 1.4 }}>
						{t('print_route_order_type_warning')}
					</p>
					{draftPrepIds.length > 0 &&
						<p className="text-danger mb-3" style={{ fontSize: '14px', lineHeight: 1.4 }}>
							{t('print_route_disable_prep_first')}
						</p>
					}
					{modalRouteDraftConflict &&
						<p className="text-danger mb-3" style={{ fontSize: '14px', lineHeight: 1.4 }}>
							{t('print_route_conflict_error', { name: modalOTS.device_name || '' })}
						</p>
					}
					<div className="addition-wrapper gap-2 no-scroll">
						{orderTypeSubs.map((item) => (
							<Fragment key={item.id}>
								<div className="d-flex justify-content-between">
									<b className="vertical-center">{item.name}</b>
									<div className="form-check form-switch form-switch-lg">
										<input type="checkbox" className="form-check-input"
											checked={draftOtsIds.includes(item.id)}
											disabled={draftPrepIds.length > 0 && !draftOtsIds.includes(item.id)}
											onChange={(e) => handleToggleOts(item.id, e.target.checked)} />
									</div>
								</div>
							</Fragment>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-outline-warning"
						onClick={() => toggleModalOTS()}>
						{t('cancel')}
					</button>
					<button type="button" className="btn btn-primary"
						disabled={modalRouteDraftConflict}
						onClick={() => saveModalOTS()}>
						{t('save')}
					</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

export default Index
